import type {
  BaseRecord,
  CrudSort,
  DataProvider,
  GetListParams,
  LogicalFilter,
  Pagination,
} from "@refinedev/core";
import { config } from "../config/config";
import { axiosInstance } from "../utils/axios";

export const API_URL = config.BASE_URL + "/api/v1";

type ListResponse<T> = {
  responseData: T[];
  pageMetadata: {
    count: number;
  };
};

const getPaginationParams = (
  pagination: Pagination | undefined,
): Record<string, string> => {
  return {
    page: ((pagination?.current ?? 1) - 1).toString(),
    size: pagination?.pageSize?.toString() ?? "",
  };
};

const getSortersParams = (
  sorters: CrudSort[] | undefined,
  defaultSort?: { field: string; order: string },
): Record<string, string> => {
  if (sorters && sorters.length > 0) {
    const sorter = sorters[0];
    return {
      sort: sorter.field,
      order: sorter.order,
    };
  }
  if (defaultSort) {
    return { sort: defaultSort.field, order: defaultSort.order };
  }
  return {};
};

const getFiltersParams = (
  filters: LogicalFilter[] | undefined,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (filters) {
    for (const filter of filters) {
      const { field, value } = filter;
      if (value !== undefined && value !== null && value !== "") {
        params[field] = String(value);
      }
    }
  }
  return params;
};

const getQueriesParams = (
  queries: { field: string; value: string }[] | undefined,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (Array.isArray(queries)) {
    for (const query of queries) {
      if (query?.field && query?.value !== undefined) {
        params[query.field] = query.value;
      }
    }
  }
  return params;
};

export const dataProvider: DataProvider = {
  getList: async <TData extends BaseRecord = BaseRecord>({
    resource,
    sorters,
    pagination,
    filters,
    meta,
  }: GetListParams) => {
    const searchParams = new URLSearchParams({
      ...getPaginationParams(pagination),
      ...getSortersParams(sorters, meta?.defaultSort),
      ...getFiltersParams(filters as LogicalFilter[]),
      ...getQueriesParams(meta?.queries),
    });

    const response = await axiosInstance.get<ListResponse<TData>>(
      `${API_URL}/${resource}?${searchParams.toString()}`,
    );

    if (response.status < 200 || response.status > 299) throw response;

    return {
      data: response.data.responseData,
      total: response.data.pageMetadata.count,
    };
  },

  getMany: async () => {
    throw new Error("Not implemented");
  },

  getOne: async ({ resource, id }) => {
    const response = await axiosInstance.get(`/api/${resource}/${id}`);

    if (response.status < 200 || response.status > 299) throw response;

    return {
      data: response.data.responseData,
    };
  },

  create: async () => {
    throw new Error("Not implemented");
  },

  update: async () => {
    throw new Error("Not implemented");
  },

  getApiUrl: () => API_URL,

  deleteOne: () => {
    throw new Error("Not implemented");
  },

  custom: async ({ url, method, payload, headers, query, meta }) => {
    const searchParams = new URLSearchParams({
      ...getQueriesParams(
        (query ?? meta?.queries) as
          | { field: string; value: string }[]
          | undefined,
      ),
    });

    const response = await axiosInstance.request({
      url: `${API_URL}/${url}?${searchParams.toString()}`,
      method,
      headers,
      data: payload,
    });

    if (response.status < 200 || response.status > 299) throw response;

    if (
      response.headers &&
      response.headers["content-type"] === "text/csv;charset=UTF-8"
    ) {
      const blob = new Blob([response.data]);
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = urlBlob;

      const contentDisposition = response.headers["content-disposition"];
      const fileNameMatch = contentDisposition?.match(
        /filename\*?=["']?(?:UTF-8'')?([^"';]+)["']?/i,
      );
      a.download = fileNameMatch?.[1] ?? "export.csv";

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(urlBlob);
    }

    return { data: response.data.responseData };
  },
};
